/**
 * attackerForm.ts — which RAID_ATTACKERS row a record's FORM is.
 *
 * WHY THIS EXISTS. Both move surfaces (movePool's PvP pool and MoveInsights'
 * PvE table) used to resolve a species with
 *
 *     RAID_ATTACKERS.find(a => a.dexId === id && a.form === null)
 *       ?? RAID_ATTACKERS.find(a => a.dexId === id)
 *
 * which is the BASE form every time, so an Armored Mewtwo was shown base
 * Mewtwo's moves and an Alolan Raichu base Raichu's. 78 of the 930 species in
 * raidMeta.generated.ts carry more than one attacker row (regionals, Therian
 * formes, Rotom appliances, Armored Mewtwo), and a record already knows which
 * one it is: `MyPokemonRecord.formId`.
 *
 * THE HARD PART IS THE MAPPING, and scripts/formReleaseGate.ts already wrote
 * down why: our form slugs come from PokéAPI (`raichu-alola`, `shellos-west`,
 * `darmanitan-galar-standard`) and the attacker rows' `form` label is derived
 * from the GO Game Master's own token
 * (`Alola`, `West Sea`, `Galarian Standard`, and a bare `A` for Armored
 * Mewtwo). Neither side spells the other's tokens, so a token is expanded into
 * an ORDERED list of candidate spellings and the first one that matches a real
 * label wins. That is the same shape as the gate's `tokenCandidates`, kept
 * separate because this one runs on device against 46 labels rather than at
 * build time against PvPoke.
 *
 * A MISS FALLS BACK TO THE BASE ROW, deliberately. Showing base Mewtwo's moves
 * for an Armored Mewtwo is bad; showing Alolan Raichu's moves for a Mega
 * Raichu would be worse, because it reads as an answer. So the matching is
 * exact-first, then a single unambiguous prefix pass, and anything else is a
 * miss.
 *
 * MEGAS ARE CURRENTLY A MISS, and that is the data rather than this file: the
 * checked-in raidMeta.generated.ts (2026-08-19) carries NO row whose form is a
 * Mega label, so `charizard-mega-y` resolves to base Charizard. The candidate
 * expansion already spells Mega X / Mega Y the way the generator's `formLabel`
 * would, so the day those rows land the match starts working; the test suite
 * pins both halves of that.
 *
 * Pure module. No DOM, no I/O.
 */
import { FORM_GROUPS_BY_SPECIES_ID } from "./generated/pokemonForms.generated";
import { RAID_ATTACKERS, type RaidAttacker } from "./generated/raidMeta.generated";

/** Lowercase, letters only. `Galarian Standard` -> `galarianstandard`. */
export function normaliseFormLabel(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z]/g, "");
}

/** Attacker rows by dex id, in file order (built once, read per render). */
const ROWS_BY_SPECIES: ReadonlyMap<number, readonly RaidAttacker[]> = (() => {
  const out = new Map<number, RaidAttacker[]>();
  for (const a of RAID_ATTACKERS) {
    const list = out.get(a.dexId);
    if (list) list.push(a);
    else out.set(a.dexId, [a]);
  }
  return out;
})();

/** `Mr Mime` -> `mr-mime`, so an attacker name can strip a form slug's prefix. */
function slugifyName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

/**
 * The form part of a form slug: `raichu-alola` -> `alola`,
 * `mr-mime-galar` -> `galar`, `charizard-mega-y` -> `mega-y`.
 *
 * The species half is stripped by NAME rather than by cutting at the first
 * hyphen, because plenty of species names carry one (`mr-mime`, `ho-oh`,
 * `type-null`). Candidate prefixes are the forms registry's own species slug
 * (which is what generated the formId) plus the attacker rows' names, so a
 * form the registry does not carry — Armored Mewtwo, every Mega — still
 * strips cleanly. Longest prefix wins.
 */
export function attackerFormToken(speciesId: number, formId: string): string {
  const rows = ROWS_BY_SPECIES.get(speciesId) ?? [];
  const prefixes = [
    FORM_GROUPS_BY_SPECIES_ID.get(speciesId)?.speciesName,
    ...rows.map((r) => slugifyName(r.name)),
  ].filter((p): p is string => !!p);
  let best = "";
  for (const p of prefixes) {
    if (formId === p) return "";
    if (formId.startsWith(p + "-") && p.length > best.length) best = p;
  }
  return best ? formId.slice(best.length + 1) : formId;
}

/**
 * Every spelling of a form token worth trying, most specific first.
 *
 * Our slugs carry a bare region (`galar`), the Game Master an adjective
 * (`Galarian`); PokéAPI files Paldean Tauros as `paldea-combat-breed` and the
 * Game Master as `Paldea Combat`; Armored Mewtwo is `A` on one side and
 * `armored` on any slug a trainer's record could carry. Order is insertion
 * order and the caller takes the first EXACT hit, so the raw token is tried
 * before any rewrite of it.
 */
function tokenCandidates(raw: string): string[] {
  const base = normaliseFormLabel(raw);
  if (!base) return [];
  const out = new Set<string>([base]);

  // Regional adjectives: ours carry the bare region, the Game Master the
  // adjective for Alola's neighbours but not for Alola itself (`RATTATA_ALOLA`
  // is labelled `Alola`, `PONYTA_GALARIAN` is `Galarian`), so both spellings
  // are offered for all four.
  const REGIONS: ReadonlyArray<[string, string]> = [
    ["alola", "alolan"],
    ["galar", "galarian"],
    ["hisui", "hisuian"],
    ["paldea", "paldean"],
  ];
  for (const [bare, adj] of REGIONS) {
    if (base.startsWith(adj)) out.add(bare + base.slice(adj.length));
    else if (base.startsWith(bare)) out.add(adj + base.slice(bare.length));
  }

  // `breed` is PokéAPI filler the Game Master does not carry:
  // `paldea-combat-breed` is `Paldea Combat`. `standard` and `normal` are the
  // reverse — filler on one side and meaningful on the other
  // (`Galarian Standard` is a real label) — so the trimmed spelling is added
  // AFTER the untrimmed one rather than replacing it.
  for (const t of [...out]) {
    const trimmed = t.replace(/(breed|standard|normal)$/, "");
    if (trimmed && trimmed !== t) out.add(trimmed);
  }

  // Armored Mewtwo: the Game Master's form suffix is a bare `A`.
  if (base === "armored" || base === "armor") out.add("a");

  return [...out];
}

/**
 * The attacker row for this species' base form: the row the Game Master left
 * unlabelled, or the first row for a species GO models only as forms
 * (Wormadam is Plant / Sandy / Trash with no plain row). Null when the
 * generated table does not carry the species at all.
 */
export function baseRaidAttacker(speciesId: number): RaidAttacker | null {
  const rows = ROWS_BY_SPECIES.get(speciesId);
  if (!rows || rows.length === 0) return null;
  return rows.find((a) => a.form === null) ?? rows[0];
}

/**
 * The attacker row for a record's species AND form, falling back to the base
 * row whenever the form cannot be matched confidently.
 *
 * `formId` is the forms-registry slug `MyPokemonRecord.formId` carries
 * (`raichu-alola`, `landorus-therian`); null / undefined means the base form,
 * which is what the detail page's picker writes for it.
 */
export function findRaidAttacker(
  speciesId: number,
  formId?: string | null,
): RaidAttacker | null {
  const rows = ROWS_BY_SPECIES.get(speciesId);
  if (!rows || rows.length === 0) return null;
  const base = baseRaidAttacker(speciesId);
  if (!formId || rows.length === 1) return base;

  const labelled = rows.filter(
    (r): r is RaidAttacker & { form: string } => r.form !== null,
  );
  if (labelled.length === 0) return base;

  const candidates = tokenCandidates(attackerFormToken(speciesId, formId));
  if (candidates.length === 0) return base;

  // Exact first: `alola` is `Alola`, `single-strike` is `Single Strike`.
  for (const c of candidates) {
    const hits = labelled.filter((r) => normaliseFormLabel(r.form) === c);
    if (hits.length === 1) return hits[0];
    if (hits.length > 1) return base; // ambiguous data: say nothing new
  }

  // Then ONE prefix pass, and only when it is unambiguous: PokéAPI's
  // `necrozma-dusk` is the Game Master's `Dusk Mane` and `zacian-crowned` its
  // `Crowned Sword`. Four letters minimum, so a stub like Mewtwo's `a` can
  // never reach across to `Alola`.
  for (const c of candidates) {
    if (c.length < 4) continue;
    const hits = labelled.filter((r) => normaliseFormLabel(r.form).startsWith(c));
    if (hits.length === 1) return hits[0];
    if (hits.length > 1) return base;
  }

  return base;
}
